import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { PhoneOutgoingIcon, XIcon } from "lucide-react";

import { Column, Row, Text } from "@/components";
import { AgenticScorePill } from "@/components/AgenticScorePill";
import { ConfirmationDialog } from "@/components/ConfirmationDialog";
import { PriorityCell } from "@/components/portfolio/table/cells/PriorityCell";
import { Button } from "@ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@ui/tooltip";

import { useAcceptCallRequest } from "@/hooks/mutations/useAcceptCallRequest";
import { useGetPopupNotifications } from "@/hooks/queries/useGetPopupNotifications";
import { useUpdateSetNotifications } from "@/hooks/updateSets/useUpdateSetNotifications";
import { useErrorToast } from "@/hooks/useErrorToast";
import { agenticScoreToBucket } from "@/lib/agenticScore";
import {
  CALL_REQUEST_WINDOW_MS,
  MS_PER_SECOND,
  SECONDS_PER_MINUTE,
} from "@/lib/constants";
import { dates } from "@/lib/dates";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import {
  MIXPANEL_CALL_REQUEST_ACTIONS,
  MIXPANEL_CALL_REQUEST_SOURCES,
} from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";
import {
  AgenticScoreBucket,
  NotificationCandidateRequestedCallPayload,
  NotificationResponse,
  NotificationType,
} from "@/types";

// Under 30 seconds left the countdown goes red — the point at which there isn't
// time to finish reading and still pick up.
const COUNTDOWN_URGENT_SECONDS = 30;

// Slack DMs link here rather than straight to Quo, so the confirmation still
// runs and the visit is attributable to a signed-in user.
const CALL_REQUEST_PARAM = "callRequest";

const EXPIRED_MESSAGE =
  "This call request expired and the AI scheduling agent is now replying.";

// Skinny notification popup at the top of the screen for a user to take a candidate requested call right now
export const CandidateCallRequestBanner = () => {
  /* Database CRUD Operations */
  // Read
  const { data: notifications = [], isPending: isLoadingNotifications } =
    useGetPopupNotifications();
  // Update
  const { mutate: updateNotifications } = useUpdateSetNotifications();
  const {
    mutate: acceptCallRequest,
    isError,
    isPending,
    reset,
  } = useAcceptCallRequest();

  /* React Hooks */
  const errorToast = useErrorToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isConfirming, setIsConfirming] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [source, setSource] = useState(MIXPANEL_CALL_REQUEST_SOURCES.BANNER);
  const hasHandledDeepLink = useRef(false);

  const callRequest = notifications.find(
    (notification) =>
      notification.type === NotificationType.CANDIDATE_REQUESTED_CALL,
  );
  const callRequestId = callRequest?.id;
  const callRequestCandidacyId = callRequest
    ? (callRequest.payload as NotificationCandidateRequestedCallPayload)
        .candidacyId
    : null;
  const msRemaining = callRequest
    ? new Date(callRequest.createdAt).getTime() + CALL_REQUEST_WINDOW_MS - now
    : 0;

  // Ticks only while a request is live; the countdown is the only thing on the
  // page that needs a second-by-second re-render.
  useEffect(() => {
    if (!callRequestId) return;
    const countdownTimer = window.setInterval(() => {
      setNow(Date.now());
    }, MS_PER_SECOND);
    return () => {
      window.clearInterval(countdownTimer);
    };
  }, [callRequestId]);

  // Arriving from the Slack DM. The prompt opens itself, or says the window
  // shut if the agent has already taken the conversation back.
  useEffect(() => {
    const deepLinkCandidacyId = searchParams.get(CALL_REQUEST_PARAM);
    if (!deepLinkCandidacyId || isLoadingNotifications) return;
    if (hasHandledDeepLink.current) return;
    hasHandledDeepLink.current = true;

    if (callRequestCandidacyId === deepLinkCandidacyId) {
      handleOpenConfirmation(
        MIXPANEL_CALL_REQUEST_SOURCES.SLACK,
        callRequestCandidacyId,
      );
    } else {
      errorToast({ description: EXPIRED_MESSAGE });
    }

    // Strip it so a refresh doesn't reopen a prompt they already answered.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(CALL_REQUEST_PARAM);
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingNotifications, callRequestCandidacyId]);

  /* Helpers */
  const secondsRemaining = Math.max(0, Math.ceil(msRemaining / MS_PER_SECOND));

  const countdownLabel = () => {
    const minutes = Math.floor(secondsRemaining / SECONDS_PER_MINUTE);
    const seconds = secondsRemaining % SECONDS_PER_MINUTE;
    return `${minutes.toString()}:${seconds.toString().padStart(2, "0")}`;
  };

  // Straight to the conversation when we have one, otherwise the owner's inbox.
  const quoUrl = (payload: NotificationCandidateRequestedCallPayload) => {
    const inboxUrl = `https://my.quo.com/inbox/${payload.quoPhoneNumberId ?? ""}`;
    if (!payload.quoConversationId) return inboxUrl;
    return `${inboxUrl}/c/${payload.quoConversationId}`;
  };

  // Accepted and cancelled inherit whatever opened the prompt; only the open
  // itself knows the source first-hand.
  const trackPrompt = (
    action: MIXPANEL_CALL_REQUEST_ACTIONS,
    candidacyId: string,
    promptSource: MIXPANEL_CALL_REQUEST_SOURCES = source,
  ) => {
    const payload = callRequest?.payload as
      | NotificationCandidateRequestedCallPayload
      | undefined;
    mixpanelUtils.trackCallRequestPrompt(action, {
      agentic_score: payload?.agenticScore ?? null,
      candidacy_id: candidacyId,
      job_priority: payload?.priority ?? null,
      seconds_remaining: secondsRemaining,
      source: promptSource,
    });
  };

  /* Handlers */
  const handleDismiss = (notificationId: number) => {
    updateNotifications({ notificationIds: [notificationId], dismiss: true });
  };

  const handleOpenConfirmation = (
    promptSource: MIXPANEL_CALL_REQUEST_SOURCES,
    candidacyId: string,
  ) => {
    reset();
    setSource(promptSource);
    setIsConfirming(true);
    trackPrompt(
      MIXPANEL_CALL_REQUEST_ACTIONS.VIEWED,
      candidacyId,
      promptSource,
    );
  };

  // Closing without confirming is the cancel-rate signal; a successful accept
  // closes the prompt through state instead, so it never lands here.
  const handleConfirmationOpenChange = (open: boolean) => {
    if (!open && callRequestCandidacyId) {
      trackPrompt(
        MIXPANEL_CALL_REQUEST_ACTIONS.CANCELLED,
        callRequestCandidacyId,
      );
    }
    setIsConfirming(open);
  };

  const handleConfirm = (notification: NotificationResponse) => {
    const payload =
      notification.payload as NotificationCandidateRequestedCallPayload;
    acceptCallRequest(
      { candidacyId: payload.candidacyId, notificationId: notification.id },
      {
        onSuccess: () => {
          trackPrompt(
            MIXPANEL_CALL_REQUEST_ACTIONS.ACCEPTED,
            payload.candidacyId,
          );
          setIsConfirming(false);
          window.open(quoUrl(payload), "_blank", "noopener,noreferrer");
        },
      },
    );
  };

  /* Render */
  const render = () => {
    if (!callRequest) return null;
    const payload =
      callRequest.payload as NotificationCandidateRequestedCallPayload;

    return (
      <>
        {msRemaining > 0 ? (
          <CallRequestBannerCard
            countdown={countdownLabel()}
            isUrgent={secondsRemaining <= COUNTDOWN_URGENT_SECONDS}
            onDismiss={() => {
              handleDismiss(callRequest.id);
            }}
            onTakeCall={() => {
              handleOpenConfirmation(
                MIXPANEL_CALL_REQUEST_SOURCES.BANNER,
                payload.candidacyId,
              );
            }}
            payload={payload}
          />
        ) : null}
        <ConfirmationDialog
          confirmButtonText="Take the call"
          description="Clicking confirm will disable the scheduling agent. This action is irreversible."
          errorMessage={EXPIRED_MESSAGE}
          isError={isError}
          isLoading={isPending}
          onConfirm={() => {
            handleConfirm(callRequest);
          }}
          onOpenChange={handleConfirmationOpenChange}
          open={isConfirming}
          title={`Call ${payload.candidateName} now?`}
          variant="square"
        >
          <Text className="text-sm font-medium text-slate-500">
            {msRemaining > 0
              ? `${countdownLabel()} left before the agent replies`
              : EXPIRED_MESSAGE}
          </Text>
        </ConfirmationDialog>
      </>
    );
  };

  return render();
};

const CallRequestBannerCard = ({
  countdown,
  isUrgent,
  onDismiss,
  onTakeCall,
  payload,
}: {
  countdown: string;
  isUrgent: boolean;
  onDismiss: () => void;
  onTakeCall: () => void;
  payload: NotificationCandidateRequestedCallPayload;
}) => {
  /* Helpers */
  const bucket = agenticScoreToBucket(payload.agenticScore);
  // Only the ratings worth interrupting someone over. Good and Okay say nothing
  // useful in the half-second this banner gets.
  const ratedWorthInterrupting =
    bucket === AgenticScoreBucket.EXCELLENT ||
    bucket === AgenticScoreBucket.GREAT;
  // Opens the candidate sheet on the job pipeline page in a new tab.
  const candidateSheetUrl = `/companies/${payload.companyId}/jobs/${payload.jobId}?c=${payload.candidacyId}`;

  /* Render */
  return (
    <Row
      className="pointer-events-auto fixed left-1/2 top-4 z-[70] w-[min(100vw-2rem,720px)] -translate-x-1/2 items-center gap-4 rounded-xl border border-slate-200 bg-white px-5 py-3.5 shadow-xl"
      data-notification-popup
    >
      <Text
        className={cn(
          "shrink-0 text-xl font-semibold tabular-nums",
          isUrgent ? "text-red-600" : "text-slate-700",
        )}
      >
        {countdown}
      </Text>

      <Column className="min-w-0 flex-1 gap-1">
        <Row className="min-w-0 items-center gap-2">
          {/* The pill's saturated border reads as a dropdown affordance in the
              portfolio table; here it's just noise, so drop it to transparent
              rather than fork the shared priority colors. */}
          {payload.priority ? (
            <Row className="shrink-0 [&_button]:border-transparent">
              <PriorityCell job={{ priority: payload.priority }} readOnly />
            </Row>
          ) : null}
          <Text className="min-w-0 truncate text-base font-semibold text-slate-900">
            {payload.reqTitle} at {payload.customerName}
          </Text>
        </Row>

        <Row className="min-w-0 items-center justify-between gap-2">
          <Row className="min-w-0 items-center gap-2">
            {ratedWorthInterrupting ? (
              <AgenticScorePill bucket={bucket} />
            ) : null}
            <Text className="min-w-0 truncate text-sm font-normal text-slate-600">
              <a
                className="hover:underline"
                href={candidateSheetUrl}
                rel="noreferrer"
                target="_blank"
              >
                {payload.candidateName}
              </a>{" "}
              is requesting a call right now
            </Text>
          </Row>
          {payload.shortlistedAt ? (
            <Text className="shrink-0 text-sm font-normal text-slate-400">
              Shortlisted: {dates.shortTimeAgo(payload.shortlistedAt)}
            </Text>
          ) : null}
        </Row>
      </Column>

      <Row className="shrink-0 items-center">
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="h-9 w-9 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                onClick={onTakeCall}
                size="icon"
                variant="ghost"
              >
                <PhoneOutgoingIcon className="h-[18px] w-[18px]" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className="z-[80]" side="bottom">
              Take the call in Quo
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="h-9 w-9 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                onClick={onDismiss}
                size="icon"
                variant="ghost"
              >
                <XIcon className="h-[18px] w-[18px]" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className="z-[80]" side="bottom">
              Dismiss
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </Row>
    </Row>
  );
};
